import React from 'react';

/* Interfaces */
import {
  LockedTipData,
  MatchFromBE,
  MatchesByID,
  PricingTierCode,
  TipData,
  UnlockedTipData,
} from '../../../../store/tips/reducer';
import { Price } from '../../../PaymentGateway/interfaces/Price';

import { PriceType } from '../../../TipPublish/interfaces/utils';
import { ShopProduct, WithPrice } from '../../shared/interfaces';

/* Classes */
import { FullDate } from './FullDate';

/* Components */
import { FreeTipComponent } from '../containers/PurchasedTipComponents/FreeTipComponent';
import { PremiumTipComponent } from '../containers/TipComponents/PremiumTipComponent';
import { PaidTipComponentConnected } from '../containers/TipComponents/PaidTipComponent';
import { PurchasedPaidTipComponent } from '../containers/PurchasedTipComponents/PurchasedPaidTipComponent';
import { PurchasedPremiumTipComponent } from '../containers/PurchasedTipComponents/PurchasedPremiumTipComponent';

/* Translations */
import i18n from '../../../../i18n';

interface TipAvailability {
  checkAvailability(): {
    isAvailable: boolean;
    message: string;
  };
}

interface IsPurchased {
  isPurchased: boolean;
}
interface WithPricingTierCode {
  pricingTierCode: PricingTierCode;
}

export interface ITipster {}

export class Tipster implements ITipster {
  public ID: string;
  public name: string;
  public avatarURL: string;
  public shareChannelURL: string;
  public sharePremiumURL: string;

  constructor(
    id: string,
    name: string,
    avatarURL: string,
    channelShareURL: string,
    premiumShareURL: string,
  ) {
    this.ID = id;
    this.name = name;
    this.avatarURL = avatarURL;
    this.shareChannelURL = channelShareURL;
    this.sharePremiumURL = premiumShareURL;
  }
}

export abstract class TipProduct
  extends ShopProduct
  implements TipAvailability
{
  tip: TipData;
  tipster: Tipster;
  abstract priceType: PriceType;
  matchesByID: MatchesByID;
  startedAt: FullDate;

  constructor(tip: TipData, tipster: Tipster, matches: MatchFromBE[]) {
    super();

    this.tip = tip;
    this.tipster = tipster;
    this.matchesByID = this.buildMatchesByID(matches);
    this.startedAt = new FullDate(tip.started_at);
  }

  private buildMatchesByID(matches: MatchFromBE[]): MatchesByID {
    return matches.reduce((acc, currentMatch) => {
      acc[currentMatch.id] = currentMatch;

      return acc;
    }, {} as MatchesByID);
  }

  abstract checkAvailability(): {
    isAvailable: boolean;
    message: string;
  };
}

export class FreeTipProduct extends TipProduct {
  component: React.ReactElement<{}> = (
    <FreeTipComponent tipster={this.tipster} tip={this} />
  );
  tip: UnlockedTipData;
  priceType: PriceType = 'FREE';

  constructor(tip: UnlockedTipData, tipster: Tipster, matches: MatchFromBE[]) {
    super(tip, tipster, matches);
    this.tip = tip;
  }

  public checkAvailability(): { isAvailable: boolean; message: string } {
    if (
      Number(new Date(this.tip.started_at)) < Number(Date.now()) &&
      !this.tip.finished_at
    ) {
      return {
        isAvailable: false,
        message: i18n.t('in_play'),
      };
    }

    return {
      isAvailable: true,
      message: '',
    };
  }
}

export class PremiumTipProduct extends TipProduct implements IsPurchased {
  component: React.ReactElement<{}>;
  tip: TipData | UnlockedTipData;
  priceType: PriceType = 'PREMIUM';
  isPurchased: boolean;

  constructor(
    tip: TipData | UnlockedTipData,
    tipster: Tipster,
    matches: MatchFromBE[],
  ) {
    super(tip, tipster, matches);
    this.isPurchased = tip.purchased;
    this.tip = this.isPurchased
      ? (tip as UnlockedTipData)
      : (tip as LockedTipData);
    this.component = this.setComponent();
  }

  private setComponent(): React.ReactElement<{}> {
    if (this.isPurchased) {
      return <PurchasedPremiumTipComponent tip={this} tipster={this.tipster} />;
    }
    return <PremiumTipComponent tip={this} tipster={this.tipster} />;
  }

  public checkAvailability(): { isAvailable: boolean; message: string } {
    if (
      Number(new Date(this.tip.started_at)) < Number(Date.now()) &&
      !this.tip.finished_at
    ) {
      return {
        isAvailable: false,
        message: i18n.t('in_play'),
      };
    }

    return {
      isAvailable: true,
      message: '',
    };
  }
}

export class PaidTipProduct
  extends TipProduct
  implements IsPurchased, WithPricingTierCode, WithPrice
{
  component: React.ReactElement<{}>;
  tip: TipData | UnlockedTipData;
  priceType: PriceType = 'PAID';
  isPurchased: boolean;
  price: Price;
  discountPercentage: number;
  priceWithDiscount: Price;
  pricingTierCode: PricingTierCode;

  constructor(
    tip: TipData | UnlockedTipData,
    tipster: Tipster,
    matches: MatchFromBE[],
  ) {
    super(tip, tipster, matches);

    this.isPurchased = tip.purchased || !!tip.result || tip.owned;
    this.tip = this.isPurchased
      ? (tip as UnlockedTipData)
      : (tip as LockedTipData);
    this.price = new Price(
      tip.web_price.amount,
      tip.web_price.currency,
      tip.web_price.decimals,
    );
    this.priceWithDiscount = new Price(
      tip.web_price_with_discount.amount,
      tip.web_price_with_discount.currency,
      tip.web_price_with_discount.decimals,
    );
    this.discountPercentage = tip.web_discount_percentage;
    this.pricingTierCode = tip.pricing_tier_code;
    this.component = this.setComponent();
  }

  private setComponent(): React.ReactElement<{}> {
    if (this.isPurchased) {
      return <PurchasedPaidTipComponent tip={this} tipster={this.tipster} />;
    }
    return <PaidTipComponentConnected tip={this} tipster={this.tipster} />;
  }

  public checkAvailability(): { isAvailable: boolean; message: string } {
    if (
      Number(new Date(this.tip.started_at)) < Number(Date.now()) &&
      !this.tip.finished_at
    ) {
      return {
        isAvailable: false,
        message: i18n.t('in_play'),
      };
    }

    if (this.tip.sales_are_stopped) {
      return {
        isAvailable: false,
        message: i18n.t('sales_are_stopped'),
      };
    }

    return {
      isAvailable: true,
      message: '',
    };
  }
}
